// Feed track-waveform source mode.
//
// Decides what the For You *track* cards render in their waveform strip. Owned
// by the server flag FEED_WAVEFORM_MODE (served via GET /v1/config/lazy-resolve)
// so it flips without an app release. Mirrors the lazy-config fetch in
// MixPlayer.js / nativeMixCover.js: a module-level mode + a subscriber hook
// that forces re-render when the mode resolves. Unlike those, this is fetched
// ONCE per session (app start) — the waveform style doesn't need to flip
// mid-session, and a re-poll would risk restyling cards under the user.
//
//   FULL   → real full-track peaks (yt-dlp / SC), eager. Historical behavior;
//            track cards show a blank reserved strip until peaks download.
//   SAMPLE → real peaks from the Apple preview sample only, eager.
//   FAKE   → synthetic detailed waveform up front; the real (full) waveform
//            lazy-loads and swaps in once the track starts playing.
//
// Waveforms are hidden entirely on mobile (see FeedTab), so this only ever
// matters on desktop + desktop web.

import { useEffect, useState } from 'react';

const LS_KEY = 'feed:waveformMode';
// Dev-only override. Set localStorage['feed:waveformMode:override'] = 'FAKE'
// (or 'SAMPLE' / 'FULL') in DevTools + reload to force a mode without a server.
// When present, the server fetch is skipped entirely so it can't overwrite the
// override. Dead-code-eliminated from production bundles (NODE_ENV gate).
const LS_OVERRIDE_KEY = 'feed:waveformMode:override';
const VALID = new Set(['FULL', 'SAMPLE', 'FAKE']);

const _isDev = process.env.NODE_ENV !== 'production';

function _readPersisted(key) {
    try {
        const v = localStorage.getItem(key);
        return VALID.has(v) ? v : null;
    } catch { return null; }
}

// Dev override wins over everything and is read fresh each time (so flipping it
// in DevTools takes effect on the next render with no reload of this module).
function _devOverride() {
    return _isDev ? _readPersisted(LS_OVERRIDE_KEY) : null;
}

// Last-known mode, persisted across launches so there's no flash of the wrong
// style on cold start after the first run. null until the first-ever fetch.
let _mode = _readPersisted(LS_KEY);
let _loaded = false;
const _subs = new Set();

function _notify() {
    for (const fn of _subs) { try { fn(); } catch { /* one subscriber must not break the rest */ } }
}

async function _pollOnce() {
    if (typeof window === 'undefined' || !window.electron?.djpal?.getClientConfig) return;
    try {
        // On Electron, getClientConfig reads the main-process _lazyConfigCache,
        // which is only populated once refreshLazyConfig has run. We can't rely
        // on MixPlayer's separate poll having completed first (import-order
        // race), so refresh the cache ourselves before reading — same pattern
        // as MixPlayer's _pollLazyConfigOnce. On web refreshLazyConfig doesn't
        // exist and getClientConfig hits the live endpoint, so this is a no-op.
        if (window.electron.djpal.refreshLazyConfig) {
            const token = (() => {
                try { return localStorage.getItem('djpal:token') || null; } catch { return null; }
            })();
            try { await window.electron.djpal.refreshLazyConfig(token); } catch { /* read whatever's cached */ }
        }
        const cfg = await window.electron.djpal.getClientConfig();
        // music-info wraps payloads in a { data, meta } envelope; tolerate both.
        const root = cfg?.data || cfg;
        const next = root?.feedWaveform?.mode;
        const mode = VALID.has(next) ? next : 'FAKE';
        const changed = mode !== _mode;
        _mode = mode;
        try { localStorage.setItem(LS_KEY, mode); } catch { /* non-fatal */ }
        if (changed) _notify();
    } catch { /* keep previous value; next poll retries */ }
    finally {
        if (!_loaded) { _loaded = true; _notify(); }
    }
}

// Fetch once at app start. No re-poll: the waveform style is a session-level
// decision, so we resolve it once and leave it. A dev override short-circuits
// the fetch so it can't be clobbered by the server value.
if (typeof window !== 'undefined' && !_devOverride()) {
    _pollOnce();
}

// Effective mode. A dev override wins outright; otherwise the server value
// (once fetched), falling back to FAKE (the current default) until it
// resolves — so a fresh install with no flag flip shows the synthetic
// waveform immediately rather than a blank strip.
function _effectiveMode() {
    return _devOverride() || _mode || 'FAKE';
}

export function getFeedWaveformMode() {
    return _effectiveMode();
}

// Subscribe a component to mode changes so it re-renders when the server flag
// loads/flips. Returns the current mode.
export function useFeedWaveformMode() {
    const [, setTick] = useState(0);
    useEffect(() => {
        const fn = () => setTick(n => n + 1);
        _subs.add(fn);
        return () => { _subs.delete(fn); };
    }, []);
    return _effectiveMode();
}
